/**
 * Recover a readable `.zwdoc` (or convertible Markdown) body from git file
 * history after the live buffer was corrupted (e.g. map JSON written into a
 * Database entry).
 */

import {
  getFileChange,
  listFileHistory,
  type FileHistoryEntry,
} from "../fileHistory";
import { just, nothing, type Maybe } from "../maybe";
import { isForeignJsonDocBody, serializeZwDoc } from "./format";
import { anyToZwDoc } from "./review";

/** True when `raw` can be opened in the prose editor (`.zwdoc` or Markdown). */
export function isRecoverableDocBody(raw: string): boolean {
  if (raw.trim().length === 0) return false;
  if (isForeignJsonDocBody(raw)) return false;
  return anyToZwDoc(raw).ok;
}

/**
 * Newest-first history → first blob that parses as a document, returned as
 * canonical `.zwdoc` text (Markdown versions are converted).
 */
export async function findLastGoodFileVersion(
  rootPath: string,
  relativePath: string,
  limit = 50,
): Promise<Maybe<string>> {
  let entries: FileHistoryEntry[];
  try {
    entries = await listFileHistory(rootPath, relativePath, limit);
  } catch {
    return nothing;
  }
  for (const entry of entries) {
    try {
      const change = await getFileChange(rootPath, relativePath, entry.oid);
      if (isRecoverableDocBody(change.after) === false) continue;
      const parsed = anyToZwDoc(change.after);
      if (parsed.ok === false) continue;
      return just(serializeZwDoc(parsed.value));
    } catch {
      continue;
    }
  }
  return nothing;
}
